import { CellContext, createColumnHelper } from "@tanstack/react-table";
import { EditIcon, TrashAltIcon } from "assets/icons";
import { Media } from "components";
import { t } from "i18next";
import { useRefetchQuery } from "libs/reactQuery";
import moment from "moment";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState
} from "react";
import toast from "react-hot-toast";
import { DateTableCell, RowDragHandleCell, TableItemActionsControl } from "ui";

import { useUpdateSpecialOffer } from "../apis";
import { OfferStatus } from "../components";
import { getDifferenceInDays } from "../helpers";
import { IOffer } from "../offers.types";

const columnHelper = createColumnHelper<IOffer>();

interface ColumnProps {
  isDragDisabled?: boolean;
  onOpenEdit: () => void;
  onOpenDelete: () => void;
  setOfferId: Dispatch<SetStateAction<string | undefined>>;
}

const useOffersColumns = ({
  isDragDisabled,
  onOpenEdit,
  onOpenDelete,
  setOfferId
}: ColumnProps) => {
  const refetchOffers = useRefetchQuery(["special-offers"]);

  const [openedItemOptionsIndex, setOpenedItemOptionsIndex] = useState<
    number | null
  >(null);

  const [openedItemStatusesIndex, setOpenedItemStatusesIndex] = useState<
    number | null
  >(null);

  const { mutate: onUpdateOfferStatus } = useUpdateSpecialOffer({
    onSuccess: () => {
      refetchOffers();
      setOpenedItemStatusesIndex(null);
      toast.success(t("Offer status was updated successfully"));
    }
  });

  const handleMenuButtonClick = useCallback(
    (
      cell: CellContext<IOffer, string | undefined>,
      handleOpenModal: () => void
    ) => {
      return () => {
        handleOpenModal();
        setOfferId(cell.getValue());
      };
    },
    [setOfferId]
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor((row) => row.title, {
        id: "dragHandle",
        header: "",
        enableSorting: false,
        size: 3,
        minSize: 3,
        cell: (cell) => (
          <RowDragHandleCell
            rowId={cell.row.id}
            isDragDisabled={isDragDisabled}
          />
        )
      }),
      columnHelper.accessor((row) => row.title, {
        id: "Title",
        header: t("Offer"),
        size: 16,
        minSize: 16,
        cell: (cell) => (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "15px",
              fontWeight: "700"
            }}
          >
            <Media
              mediaFile={cell.row.original.image}
              withVideoLabel
              style={{
                width: 52,
                height: 52,
                borderRadius: "4px"
              }}
            />
            <p>{cell.getValue()}</p>
          </div>
        )
      }),
      columnHelper.accessor((row) => row.from, {
        id: "From",
        header: t("From"),
        size: 9,
        minSize: 9,
        cell: (cell) => <DateTableCell date={cell.getValue()} />
      }),
      columnHelper.accessor((row) => row.to, {
        id: "To",
        header: t("To"),
        size: 9,
        minSize: 9,
        cell: (cell) => <DateTableCell date={cell.getValue()} />
      }),
      columnHelper.accessor((row) => row.from, {
        id: "daysLeft",
        header: t("Days left"),
        enableSorting: false,
        size: 8,
        minSize: 8,
        cell: (cell) =>
          getDifferenceInDays(moment().toISOString(), cell.row.original.to)
      }),
      columnHelper.accessor((row) => row.createdAt, {
        id: "CreatedAt",
        header: t("Date created"),
        size: 10,
        minSize: 10,
        cell: (cell) => <DateTableCell date={cell.getValue()} />
      }),
      columnHelper.accessor((row) => row.status, {
        id: "Status",
        header: t("Status"),
        size: 10,
        minSize: 10,
        cell: (cell) => (
          <OfferStatus
            offerId={cell.row.original.id}
            offerStatus={cell.getValue()}
            rowIndex={cell.row.index}
            openedItemStatusesIndex={openedItemStatusesIndex}
            setOpenedItemStatusesIndex={setOpenedItemStatusesIndex}
            onUpdateStatus={onUpdateOfferStatus}
            fromDate={cell.row.original.from}
            toDate={cell.row.original.to}
          />
        )
      }),
      columnHelper.accessor((row) => row.id, {
        header: "",
        id: "action",
        enableSorting: false,
        size: 3,
        minSize: 3,
        cell: (cell) => (
          <TableItemActionsControl
            actionsList={[
              {
                label: "Edit",
                Icon: <EditIcon fill="#090914" width={18} height={18} />,
                onClick: handleMenuButtonClick(cell, onOpenEdit)
              },
              {
                label: "Delete",
                Icon: <TrashAltIcon fill="#ff0033" width={18} height={18} />,
                labelColor: "#ff0033",
                onClick: handleMenuButtonClick(cell, onOpenDelete)
              }
            ]}
            rowIndex={cell.row.index}
            setOpenedItemOptionsIndex={setOpenedItemOptionsIndex}
            openedItemOptionsIndex={openedItemOptionsIndex}
          />
        )
      })
    ],
    [
      handleMenuButtonClick,
      onOpenDelete,
      onOpenEdit,
      onUpdateOfferStatus,
      openedItemOptionsIndex,
      openedItemStatusesIndex,
      isDragDisabled
    ]
  );

  return { columns };
};

export default useOffersColumns;
